import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type LiveSessionView = {
  lessonId: string;
  /** CRS-11: false while the school has no live provider — nothing can start or be joined. */
  configured?: boolean;
  session: {
    id: string;
    status: string;
    scheduledStartAt: string | null;
    startedAt: string | null;
  } | null;
  canStart: boolean;
  canJoin: boolean;
  canEnd: boolean;
  canWatchRecording: boolean;
  joinUrl: string | null;
  isHost: boolean;
};

type Props = {
  lessonId: string;
  hasVideo: boolean;
};

export function LiveSessionPanel({ lessonId, hasVideo }: Props) {
  const t = useTranslations("liveSession");
  const tRoot = useTranslations();
  // C4: show the translated error for the server's code, never its English message.
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `liveSession.${fallbackKey}`);
  const [view, setView] = useState<LiveSessionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const res = await apiFetch(`/v1/lessons/${lessonId}/live-session`, { signal });
        const json = (await res.json()) as { data?: LiveSessionView };
        if (!res.ok || !json.data) {
          setError(res.ok ? t("loadFailed") : apiErrorText(json, tRoot, "liveSession.loadFailed"));
          return;
        }
        setView(json.data);
        setError(null);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setError(t("loadFailed"));
      }
    },
    [lessonId, t, tRoot],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      setLoading(true);
      await load(controller.signal);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [load]);

  async function postAction(url: string) {
    setActing(true);
    setError(null);
    try {
      const res = await apiFetch(url, { method: "POST" });
      if (!res.ok) {
        setError(errorText(await res.json().catch(() => null), "actionFailed"));
        return;
      }
      await load();
    } finally {
      setActing(false);
    }
  }

  async function joinLive() {
    setActing(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/lessons/${lessonId}/live-session/join`, {
        method: "POST",
      });
      const json = (await res.json()) as { data?: { redirectUrl: string } };
      if (!res.ok || !json.data?.redirectUrl) {
        setError(res.ok ? t("joinFailed") : errorText(json, "joinFailed"));
        return;
      }
      window.location.href = json.data.redirectUrl;
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-[var(--text-muted)]" data-testid="live-session-loading">
        {t("loading")}
      </p>
    );
  }

  const status = view?.session?.status ?? "scheduled";

  if (view?.configured === false) {
    return (
      <section
        className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
        data-testid="live-session-panel"
      >
        <h2 className="text-lg font-medium">{t("title")}</h2>
        <p
          className="mt-1 text-sm text-[var(--text-secondary)]"
          data-testid="live-session-not-configured"
        >
          {t("notConfigured")}
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
      data-testid="live-session-panel"
    >
      <h2 className="text-lg font-medium">{t("title")}</h2>
      <p className="mt-1 text-sm text-[var(--text-secondary)]" data-testid="live-session-status">
        {t(`status.${status}`)}
      </p>

      {view?.session?.scheduledStartAt ? (
        <p className="mt-1 text-xs text-[var(--text-muted)]" data-testid="live-session-scheduled">
          {t("scheduledFor", {
            date: new Date(view.session.scheduledStartAt).toLocaleString(),
          })}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-3">
        {view?.canStart ? (
          <button
            type="button"
            disabled={acting}
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
            data-testid="live-session-start"
            onClick={() => postAction(`/v1/instructor/lessons/${lessonId}/live-session/start`)}
          >
            {acting ? t("working") : t("startLive")}
          </button>
        ) : null}

        {view?.canJoin && !view.isHost ? (
          <button
            type="button"
            disabled={acting}
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
            data-testid="live-session-join"
            onClick={() => joinLive()}
          >
            {acting ? t("working") : t("joinLive")}
          </button>
        ) : null}

        {view?.canJoin && view.isHost && view.joinUrl ? (
          <a
            href={view.joinUrl}
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)]"
            data-testid="live-session-host-room"
          >
            {t("openHostRoom")}
          </a>
        ) : null}
      </div>

      {view?.canWatchRecording && hasVideo ? (
        <p
          className="mt-3 text-sm text-green-700 dark:text-green-400"
          data-testid="live-session-recording-ready"
        >
          {t("recordingReady")}
        </p>
      ) : null}

      {error ? (
        <p className="mt-3 text-sm text-[var(--danger)]" data-testid="live-session-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
